import type { Question } from '../types';

/**
 * 题库元信息
 */
export interface BankMeta {
  id: string;
  name: string;
  description?: string;
  version: string;
  examType: string;
  section?: string;
  questionCount: number;
  downloadedAt: string;
  updatedAt: string;
}

/**
 * 本地存储的题库数据
 */
export interface LocalBank {
  meta: BankMeta;
  questions: Question[];
}

/**
 * 题库下载响应数据（来自后端 API）
 */
export interface BankDownloadData {
  bank: {
    id: string;
    name: string;
    description?: string;
    version: string;
    examType: string;
    section?: string;
    questionCount: number;
    updatedAt: string;
  };
  questions: Question[];
  audioFiles: Array<{
    questionId: string;
    url: string;
  }>;
  metadata: {
    downloadedAt: string;
    questionsCount: number;
    audioFilesCount: number;
  };
}

/**
 * 题库索引项
 */
export interface BankIndexItem {
  id: string;
  name: string;
  version: string;
  examType: string;
  section?: string;
  questionCount: number;
  downloadedAt: string;
  size?: number;
}

/**
 * 存储使用情况
 */
export interface StorageUsage {
  totalSize: number;
  banks: Array<{
    id: string;
    name: string;
    size: number;
  }>;
}

/**
 * 存储后端接口（用于适配不同环境）
 */
export interface StorageBackend {
  /** 读取文件 */
  readFile(path: string): Promise<string | null>;
  /** 写入文件 */
  writeFile(path: string, content: string): Promise<void>;
  /** 删除文件 */
  deleteFile(path: string): Promise<void>;
  /** 检查文件是否存在 */
  exists(path: string): Promise<boolean>;
  /** 创建目录 */
  mkdir(path: string): Promise<void>;
  /** 删除目录 */
  rmdir(path: string): Promise<void>;
  /** 列出目录内容 */
  readdir(path: string): Promise<string[]>;
}

/**
 * LocalStorage 后端实现（用于 Web 环境）
 */
export class LocalStorageBackend implements StorageBackend {
  private prefix: string;

  constructor(prefix = 'examflow_banks') {
    this.prefix = prefix;
  }

  private getKey(path: string): string {
    return `${this.prefix}:${path}`;
  }

  async readFile(path: string): Promise<string | null> {
    return localStorage.getItem(this.getKey(path));
  }

  async writeFile(path: string, content: string): Promise<void> {
    localStorage.setItem(this.getKey(path), content);
  }

  async deleteFile(path: string): Promise<void> {
    localStorage.removeItem(this.getKey(path));
  }

  async exists(path: string): Promise<boolean> {
    return localStorage.getItem(this.getKey(path)) !== null;
  }

  async mkdir(_path: string): Promise<void> {
    // LocalStorage 不需要创建目录
  }

  async rmdir(path: string): Promise<void> {
    // 删除所有以该路径为前缀的项
    const prefix = this.getKey(path);
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(prefix)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
  }

  async readdir(_path: string): Promise<string[]> {
    // LocalStorage 无法列出目录，返回空数组
    return [];
  }
}

/**
 * 题库本地存储管理器
 *
 * 存储结构：
 * - banks-index.json: 已下载题库索引
 * - banks/{bankId}/meta.json: 题库元信息
 * - banks/{bankId}/questions.json: 题目数据
 */
export class BankStorage {
  private backend: StorageBackend;
  private indexPath = 'banks-index.json';

  constructor(backend?: StorageBackend) {
    this.backend = backend || new LocalStorageBackend();
  }

  /**
   * 获取已下载的题库列表
   */
  async getDownloadedBanks(): Promise<BankIndexItem[]> {
    const content = await this.backend.readFile(this.indexPath);
    if (!content) {
      return [];
    }
    try {
      const banks = JSON.parse(content) as BankIndexItem[];
      if (!Array.isArray(banks) || banks.length === 0) {
        return [];
      }

      // 过滤/清理索引残留：索引里有，但实际文件不存在（下载中断、存储被清理等）
      const validity = await Promise.all(
        banks.map(async (b) => {
          const basePath = `banks/${b.id}`;
          const metaExists = await this.backend.exists(`${basePath}/meta.json`);
          const questionsExists = await this.backend.exists(`${basePath}/questions.json`);
          return metaExists && questionsExists;
        })
      );

      const filtered = banks.filter((_, idx) => validity[idx]);
      if (filtered.length !== banks.length) {
        // 写回修正后的索引，避免后续继续误判“已下载”
        await this.backend.writeFile(this.indexPath, JSON.stringify(filtered, null, 2));
      }

      return filtered;
    } catch {
      return [];
    }
  }

  /**
   * 保存题库数据到本地
   */
  async saveBank(data: BankDownloadData): Promise<void> {
    const bankId = data.bank.id;
    const bankPath = `banks/${bankId}`;

    // 创建目录
    await this.backend.mkdir(bankPath);

    // 保存元信息
    const meta: BankMeta = {
      id: data.bank.id,
      name: data.bank.name,
      description: data.bank.description,
      version: data.bank.version,
      examType: data.bank.examType,
      section: data.bank.section,
      questionCount: data.bank.questionCount,
      downloadedAt: data.metadata.downloadedAt,
      updatedAt: data.bank.updatedAt,
    };
    await this.backend.writeFile(`${bankPath}/meta.json`, JSON.stringify(meta, null, 2));

    // 保存题目数据
    await this.backend.writeFile(
      `${bankPath}/questions.json`,
      JSON.stringify(data.questions, null, 2)
    );

    // 更新索引
    await this.updateIndex(meta);
  }

  /**
   * 获取本地题库数据
   */
  async getBank(bankId: string): Promise<LocalBank | null> {
    const bankPath = `banks/${bankId}`;

    console.log('[BankStorage] Reading bank data:', { bankId, bankPath });

    // 读取元信息
    const metaContent = await this.backend.readFile(`${bankPath}/meta.json`);
    if (!metaContent) {
      console.warn('[BankStorage] Meta file not found:', `${bankPath}/meta.json`);
      return null;
    }

    // 读取题目数据
    const questionsContent = await this.backend.readFile(`${bankPath}/questions.json`);
    if (!questionsContent) {
      console.warn('[BankStorage] Questions file not found:', `${bankPath}/questions.json`);
      return null;
    }

    console.log('[BankStorage] Files read successfully:', {
      metaContentLength: metaContent.length,
      questionsContentLength: questionsContent.length,
    });

    try {
      const meta = JSON.parse(metaContent);
      const questions = JSON.parse(questionsContent);

      // 验证元信息
      if (!meta || typeof meta !== 'object') {
        console.error('[BankStorage] Invalid meta format:', { meta });
        return null;
      }

      // 验证题目数据
      if (!Array.isArray(questions)) {
        console.error('[BankStorage] Questions is not an array:', {
          questionsType: typeof questions,
          questions: questions,
        });
        return null;
      }

      if (questions.length === 0) {
        console.warn('[BankStorage] Questions array is empty:', { bankId });
      }

      // 验证题目数据完整性（检查前几个题目）
      const sampleSize = Math.min(3, questions.length);
      for (let i = 0; i < sampleSize; i++) {
        const q = questions[i];
        if (!q || typeof q !== 'object') {
          console.error('[BankStorage] Invalid question format at index', i, ':', q);
          return null;
        }
        if (!q.id) {
          console.error('[BankStorage] Question missing id at index', i, ':', q);
          return null;
        }
      }

      console.log('[BankStorage] Bank data validated successfully:', {
        bankId,
        metaId: meta.id,
        metaName: meta.name,
        questionsCount: questions.length,
      });

      return {
        meta,
        questions,
      };
    } catch (error) {
      console.error('[BankStorage] Failed to parse bank data:', {
        bankId,
        error: error instanceof Error ? error.message : String(error),
        metaContentPreview: metaContent.substring(0, 100),
        questionsContentPreview: questionsContent.substring(0, 100),
      });
      return null;
    }
  }

  /**
   * Merge an incremental Content Inventory delta into the local package.
   */
  async mergeBankDelta(
    bankId: string,
    delta: {
      latestVersion: string;
      added: Question[];
      deletedIds: string[];
      bank?: Partial<BankMeta> & { name?: string; examType?: string };
    }
  ): Promise<{ deletedIds: string[] }> {
    const existing = await this.getBank(bankId);
    const deletedSet = new Set(delta.deletedIds);
    const kept = (existing?.questions ?? []).filter((q) => !deletedSet.has(q.id));
    const byId = new Map(kept.map((q) => [q.id, q]));
    for (const q of delta.added) {
      byId.set(q.id, q);
    }
    const questions = Array.from(byId.values());
    const nowIso = new Date().toISOString();
    const meta: BankMeta = {
      id: bankId,
      name: delta.bank?.name || existing?.meta.name || bankId,
      description: delta.bank?.description ?? existing?.meta.description,
      version: delta.latestVersion,
      examType: delta.bank?.examType || existing?.meta.examType || '',
      section: delta.bank?.section ?? existing?.meta.section,
      questionCount: questions.length,
      downloadedAt: existing?.meta.downloadedAt || nowIso,
      updatedAt: nowIso,
    };

    const bankPath = `banks/${bankId}`;
    await this.backend.mkdir(bankPath);
    await this.backend.writeFile(`${bankPath}/meta.json`, JSON.stringify(meta, null, 2));
    await this.backend.writeFile(`${bankPath}/questions.json`, JSON.stringify(questions, null, 2));
    await this.updateIndex(meta);

    return { deletedIds: delta.deletedIds };
  }

  /**
   * 获取本地题库元信息
   */
  async getBankMeta(bankId: string): Promise<BankMeta | null> {
    const bankPath = `banks/${bankId}`;
    const metaContent = await this.backend.readFile(`${bankPath}/meta.json`);
    if (!metaContent) {
      return null;
    }
    try {
      return JSON.parse(metaContent);
    } catch {
      return null;
    }
  }

  /**
   * 删除本地题库
   */
  async deleteBank(bankId: string): Promise<void> {
    const bankPath = `banks/${bankId}`;

    // 删除题库目录
    await this.backend.rmdir(bankPath);

    // 更新索引
    await this.removeFromIndex(bankId);
  }

  /**
   * 获取本地版本号
   */
  async getLocalVersion(bankId: string): Promise<string | null> {
    const meta = await this.getBankMeta(bankId);
    return meta?.version || null;
  }

  /**
   * 检查题库是否已下载
   */
  async isDownloaded(bankId: string): Promise<boolean> {
    const banks = await this.getDownloadedBanks();
    return banks.some((b) => b.id === bankId);
  }

  /**
   * 获取存储使用情况
   */
  async getStorageUsage(): Promise<StorageUsage> {
    const banks = await this.getDownloadedBanks();
    const result: StorageUsage = {
      totalSize: 0,
      banks: [],
    };

    for (const bank of banks) {
      const localBank = await this.getBank(bank.id);
      if (localBank) {
        const size = JSON.stringify(localBank).length;
        result.totalSize += size;
        result.banks.push({
          id: bank.id,
          name: bank.name,
          size,
        });
      }
    }

    return result;
  }

  /**
   * 更新索引
   */
  private async updateIndex(meta: BankMeta): Promise<void> {
    const banks = await this.getDownloadedBanks();
    const existingIndex = banks.findIndex((b) => b.id === meta.id);

    const indexItem: BankIndexItem = {
      id: meta.id,
      name: meta.name,
      version: meta.version,
      examType: meta.examType,
      section: meta.section,
      questionCount: meta.questionCount,
      downloadedAt: meta.downloadedAt,
    };

    if (existingIndex >= 0) {
      banks[existingIndex] = indexItem;
    } else {
      banks.push(indexItem);
    }

    await this.backend.writeFile(this.indexPath, JSON.stringify(banks, null, 2));
  }

  /**
   * 从索引中移除
   */
  private async removeFromIndex(bankId: string): Promise<void> {
    const banks = await this.getDownloadedBanks();
    const filtered = banks.filter((b) => b.id !== bankId);
    await this.backend.writeFile(this.indexPath, JSON.stringify(filtered, null, 2));
  }

  /**
   * 清空所有本地题库
   */
  async clearAll(): Promise<void> {
    const banks = await this.getDownloadedBanks();
    for (const bank of banks) {
      await this.backend.rmdir(`banks/${bank.id}`);
    }
    await this.backend.deleteFile(this.indexPath);
  }
}
